import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import TextInput from './inputs/text-input'
import TextArea from './inputs/text-area'
import PhotoInput from './inputs/photo-input'
import SelectInput from './inputs/select-input'
import MultiSelect from './inputs/multi-select'

export default function TeamInfo({token, league, type, player, team, onComplete}) {
  const playerPosition = () => {
    if (!player.prefer_position) return

    return player.prefer_position.split(', ').map(position => ({ value: position, label: position }))
  }

  const playerDefaults = () => {
    if (player.new) return {}
    
    return {
      skill_level: player.skill_level,
      shirt_size: player.shirt_size,
      prefer_position: playerPosition()
    }
  }
  const { register, control, handleSubmit, setValue, getValues, formState: { errors } } = useForm({ 
    defaultValues: team || playerDefaults()
  })
  const requiredMsg = 'Is required'
  const photoUrl = getValues('photo_url')
  const [fileLoaded, setFileLoaded] = useState(false)
  const colorOptions = [ 
    { value: 'Red', label: 'Red', className: 'color-value red' },
    { value: 'Blue', label: 'Blue', className: 'color-value blue' },
    { value: 'Green', label: 'Green', className: 'color-value green' },
    { value: 'Yellow', label: 'Yellow', className: 'color-value dark-color yellow' },
    { value: 'Purple', label: 'Purple', className: 'color-value purple' },
    { value: 'Pink', label: 'Pink', className: 'color-value pink' },
    { value: 'Orange', label: 'Orange', className: 'color-value orange' },
    { value: 'Brown', label: 'Brown', className: 'color-value brown' },
    { value: 'Black', label: 'Black', className: 'color-value black' },
    { value: 'White', label: 'White', className: 'color-value dark-color white' },
  ]
  const skillLevelOptions = [['', 'Select'], [0, 'Beginner'], [1, 'Intermediate'], [2, 'Advanced'], [3, 'Expert']]
  const sizeOptions = [['', 'Select'], ['XS', 'XS'], ['S', 'S'], ['M', 'M'], ['L', 'L'], ['XL', 'XL'], ['XXL', 'XXL']]
  const positionOptions = ['Forward', 'Winger', 'Center Midfielder', 'Right / Left Back', 'Center Back', 'Goalie'].map(val => ({value: val, label: val}))

  const validateText = (value) => {
    if (!value.trim()) return requiredMsg
  }

  const loadFile = (file) => {
    const fr = new FileReader()
    fr.onloadend = function() { 
      setValue('photo_url', fr.result)
      setFileLoaded(file.name)
    }
    fr.readAsDataURL(file)
  }

  const fileSelected = (file) => {
    if (file.size > 10000000) return window.alert('File size limit is 10MB')
    if (!['image/jpeg', 'image/png'].includes(file.type)) return window.alert(' Only JPG and PNG files are allowed')

    loadFile(file)
  }

  const prepareFormData = (data) => {
    let formData = new FormData()
    formData.append('type', type)
    formData.append('token', token)
    formData.append('team[name]', data.name.trim())
    formData.append('team[notes]', data.notes.trim())
    if (data.photo_url) formData.append('team[photo_url]', data.photo_url)
    formData.append('team[color]', data.color ? data.color.map(v => v.label).join(', ') : '')
    formData.append('team[registered]', true)
    formData.append('team[members_attributes][0][league_id]', league.id)
    formData.append('team[members_attributes][0][player_id]', player.id)
    formData.append('team[members_attributes][0][captain]', true)
    formData.append('team[members_attributes][0][registered]', true)
    if (data.shirt_size) {
      formData.append('player[skill_level]', data.skill_level)
      formData.append('player[prefer_position]', data.prefer_position ? data.prefer_position.map(v => v.label).join(', ') : '')
      formData.append('player[shirt_size]', data.shirt_size)
    }
    return formData
  }

  const onSubmit = (data) => {
    const formData = prepareFormData(data)
    onComplete(data, formData)
  }

  return <form className='team-info-form' onSubmit={handleSubmit(onSubmit)}>
    <div className='row'>
      <div className='col-xs-12 text-center greeting-message'>You are going to join this <b>{league.name}</b> league.</div>
    </div>

    { !player.new &&
      <div className='row'>
        <div className='col-xs-12 text-center confirm-message'>Please confirm your information</div>
        <div className='col-xs-12 text-center'><div className='your-info-section'>Your Info</div></div>
        <div className='col-xs-12'>
          <TextInput
            disabled
            label='First Name'
            value={player.first_name}
          />
        </div>
        <div className='col-xs-12'>
          <TextInput
            disabled
            label='Last Name'
            value={player.last_name}
          />
        </div>
        <div className='col-xs-12'>
          <TextInput
            disabled
            label='Email'
            value={player.email}
          />
        </div>
        <div className='col-xs-12'>
          <SelectInput
            {...register('skill_level', { required: requiredMsg })}
            autoFocus
            label='Skill Level'
            options={skillLevelOptions}
            error={errors.skill_level}
            onChange={e => setValue('skill_level', e.target.value)}
          />
        </div>
        <div className='col-xs-12'>
          <MultiSelect
            name='prefer_position'
            label='Preferred Position'
            required
            error={errors.prefer_position}
            options={positionOptions}
            control={control}
          />
        </div>
        <div className='col-xs-12'>
          <SelectInput
            {...register('shirt_size', { required: requiredMsg })}
            label='Shirt Size'
            options={sizeOptions}
            error={errors.shirt_size}
            onChange={e => setValue('shirt_size', e.target.value)}
          />
        </div>
      </div>
    }

    <div className='row'>
      <div className='col-xs-12 text-center'><div className='your-info-section'>Team Information</div></div>
      <div className='col-xs-12'>
        <TextInput
          {...register('name', { required: requiredMsg, validate: validateText })}
          autoFocus={player.new}
          label='Team Name'
          error={errors.name}
          onChange={e => setValue('name', e.target.value)}
        />
      </div>
      <PhotoInput photoUrl={photoUrl} onFileLoad={fileSelected} />
      <div className='col-xs-12 team-colors'>
        <MultiSelect 
          name='color'
          label='Top 3 Uniform Color Choices'
          options={colorOptions}
          desc='Select up to 3 colors'
          limit={3}
          control={control}
        />
      </div>
      <div className='col-xs-12'>
        <TextArea
          {...register('notes')}
          label='Notes'
          rows='4'
          onChange={(e) => setValue('notes', e.target.value)}
        />
      </div>
    </div>

    <div className='row buttons-section'>
      <div className='col-xs-12 text-center'>
        <button className='registration-button'>Next</button>
      </div>
    </div>
  </form>
}
